import { Box, Divider, Typography } from '@mui/material';
import { MedicalNoteActivity, MedicalNoteType, User, getAllDiagnosis } from '@uplift/api';
import { CalendarDateService } from '@uplift/services';
import { DL, DLItem, Loader, Markdown } from '@uplift/ui-v2';
import React, { useMemo } from 'react';
import { useProviderCptCodesQuery } from '@uplift/queries';
import { useQuery } from '@tanstack/react-query';
import { MedicalNoteSubmittedBy } from './MedicalNoteSubmittedBy.component';

export type CommunicationNoteReviewProps = {
  note: MedicalNoteActivity<MedicalNoteType.communicationNote>;
  provider: Pick<User, 'firstName' | 'lastName' | 'id'>;
};

export const CommunicationNoteReview: React.VFC<CommunicationNoteReviewProps> = ({ note, provider }) => {
  const { data: { cptCodes = [] } = {}, isLoading: cptLoading } = useProviderCptCodesQuery(provider.id);
  const { data: diagnosisData, isLoading: diagnosisLoading } = useQuery({
    queryKey: [ 'all-diagnosis' ],
    queryFn: getAllDiagnosis
  });

  const contactMethod = note.form['contactMethod'].key;
  const participant = note.form['contactParticipants'].key;

  const participantName = useMemo(() => {
    if (participant === 'client') {
      return null;
    }

    switch (contactMethod) {
      case 'email':
        return note.form['participantNameEmail'].value;
      case 'phone':
        return note.form['participantNamePhone'].value;
      case 'fax':
        return note.form['participantNameFax'].value;
      default:
        return 'Unknown';
    }
  }, [ contactMethod, note.form, participant ]);

  const details = useMemo(() => {
    if (contactMethod === 'email') {
      return <DLItem t="Email address" d={note.form['contactEmail'].value} />;
    }

    if (contactMethod === 'phone') {
      return (
        <>
          {participant !== 'client' && <DLItem t="Phone number" d={note.form['contactPhone'].value} />}
          <DLItem t="Length of call" d={note.form['lengthOfCall'].value} />
        </>
      );
    }

    if (contactMethod === 'fax') {
      return <DLItem t="Fax number" d={note.form['contactFax'].value} />;
    }

    return null;
  }, [ contactMethod, note.form, participant ]);

  const billing = useMemo(() => {
    if (!note.form['billable'].key) {
      return null;
    }

    if (diagnosisLoading || cptLoading) {
      return <Loader />;
    }

    const cptCode = cptCodes.find(x => x.id === note.cptCodeIds[0]);
    const diagnoses = diagnosisData?.data.filter(x => note.diagnosisIds.includes(x.id));
    const diagnosisList = (
      <Box>
        {diagnoses?.map(diagnosis => (
          <dd key={diagnosis.id} style={{ margin: '0' }}>{`${diagnosis?.code} ${diagnosis?.name}`}</dd>
        ))}
      </Box>
    );

    return (
      <>
        <div>
          <Typography variant="overline" color="secondary">
            Billing
          </Typography>
          <DL>
            <DLItem t="CPT Code" d={`${cptCode?.code}: ${cptCode?.name}`} />
            <DLItem t="Diagnosis" d={diagnosisList} />
          </DL>
        </div>
        <Divider />
      </>
    );
  }, [ cptCodes, cptLoading, diagnosisData?.data, diagnosisLoading, note.form, note.cptCodeIds, note.diagnosisIds ]);

  const riskAssessment = useMemo(() => {
    if (participant !== 'client') {
      return null;
    }

    const denies = note.riskAssessmentForm.fields.find(x => x.id === 'deny_all_areas_of_risk');

    if (denies?.value) {
      return null;
    }

    return (
      <>
        <div>
          <Typography variant="overline" color="secondary">
            Risk Assessment
          </Typography>
          <Markdown>
            {note.riskAssessmentForm.note}
          </Markdown>
        </div>
        <Divider />
      </>
    );
  }, [ note.riskAssessmentForm.fields, note.riskAssessmentForm.note, participant ]);

  return (
    <Box display="flex" gap="24px" flexDirection="column">
      <div>
        <Typography variant="overline" color="secondary">
          Date of Communication
        </Typography>
        <Typography>
          {CalendarDateService.date(note.form['communicationDate'].value).format('LL')}
        </Typography>
      </div>
      <Divider />
      <div>
        <Typography variant="overline" color="secondary">
          Reason for contact
        </Typography>
        <Typography>
          {note.form['contactReason'].value}
        </Typography>
      </div>
      <Divider />
      <div>
        <Typography variant="overline" color="secondary">
          Participant Details
        </Typography>
        <Typography>
          <DL>
            <DLItem t="Participants" d={note.form['contactParticipants'].value} />
            {participantName && <DLItem t="Participant's name" d={participantName} />}
            <DLItem t="Method of contact" d={note.form['contactMethod'].value} />
            {details}
          </DL>
        </Typography>
      </div>
      <Divider />
      {riskAssessment}
      {billing}
      <div>
        <Typography variant="overline" color="secondary">
          Conversation Summary
        </Typography>
        <Typography>
          {note.form['summary'].value}
        </Typography>
      </div>
      <MedicalNoteSubmittedBy user={provider} submittedAt={note.submittedAt} />
    </Box>
  );
};
